import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, CssBaseline, InputAdornment, Paper, TextField, Typography } from "@material-ui/core";
import { Email, Lock, PersonAdd, Phone, VpnKey } from "@material-ui/icons";
import { openApi } from "../../services/api";
import AuthCredit from "../../components/AuthCredit";

const card = { width: "100%", maxWidth: 440, borderRadius: 18, padding: 28, boxShadow: "0 24px 70px #1e1b4b22" };
const errorText = (err) => err?.response?.data?.error || err?.response?.data?.message || "No se pudo completar. Intenta de nuevo.";

/** Enlace personal de un plan WaToolX ya pagado: crear la cuenta o activarlo en una existente. */
export default function ActivatePlan() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("choose");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    openApi.get(`/watoolx-activation/${encodeURIComponent(token)}`)
      .then(({ data }) => { setInfo(data); if (data?.whatsapp) setWhatsapp(data.whatsapp); })
      .catch((err) => setError(errorText(err)));
  }, [token]);

  const publicidad = info?.kind === "publicidad";
  const activatePublicidad = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await openApi.post(`/watoolx-activation/${encodeURIComponent(token)}/publicidad`, { email, whatsapp });
      setDone(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const activateExisting = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await openApi.post(`/watoolx-activation/${encodeURIComponent(token)}/existing`, { email, password });
      setDone(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const planLine = !info ? "" : publicidad
    ? `${info.months} ${info.months === 1 ? "mes" : "meses"} de acceso`
    : `${info.plan?.users} usuarios · ${info.plan?.connections} líneas · ${info.months} ${info.months === 1 ? "mes" : "meses"}`;

  return (
    <Box style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "linear-gradient(135deg,#eef2ff,#f5f3ff 50%,#ecfeff)" }}>
      <CssBaseline />
      <Paper style={card}>
        <Typography style={{ fontSize: 24, fontWeight: 800, color: "#1e1b4b", marginBottom: 6 }}>{publicidad ? "Activa tu Publicidad IA" : "Activa tu plan WaToolX"}</Typography>
        {!info && !error && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
        {error && !done && <Box style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 10, padding: "10px 12px", margin: "12px 0", fontSize: 14 }}>{error}</Box>}

        {done && publicidad ? (
          <Box>
            <Box style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 12, padding: 16, margin: "14px 0" }}>
              <Typography style={{ color: "#065f46", fontWeight: 800, fontSize: 18 }}>✅ ¡{done.plan} activo!</Typography>
              <Typography variant="body2" style={{ color: "#047857", marginTop: 4 }}>
                {done.existing ? `Lo sumamos a tu cuenta (${done.email}). Entra con tu correo y contraseña de siempre.` : `Tu cuenta quedó creada con ${done.email}.`}
              </Typography>
            </Box>
            <Button href={done.accessUrl || done.siteUrl} target="_blank" rel="noopener noreferrer" fullWidth variant="contained" color="primary" size="large">
              {done.accessUrl ? "Entrar a Publicidad IA" : "Ir a Publicidad IA"}
            </Button>
          </Box>
        ) : done ? (
          <Box>
            <Box style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 12, padding: 16, margin: "14px 0" }}>
              <Typography style={{ color: "#065f46", fontWeight: 800, fontSize: 18 }}>✅ ¡Plan {done.plan} activo!</Typography>
              <Typography variant="body2" style={{ color: "#047857", marginTop: 4 }}>
                Empresa {done.company?.name} · activo hasta el {done.dueLabel}.
              </Typography>
            </Box>
            <Button component={RouterLink} to="/login" fullWidth variant="contained" color="primary" size="large">Entrar a WaToolX</Button>
          </Box>
        ) : info && (
          <>
            <Box style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 12, padding: 14, margin: "12px 0 18px" }}>
              <Typography style={{ color: "#3730a3", fontWeight: 700 }}>Plan {info.plan?.name} · pedido #{info.orderId}</Typography>
              <Typography variant="body2" style={{ color: "#4338ca" }}>{planLine}</Typography>
            </Box>

            {info.status === "used" && (
              <>
                <Typography variant="body2" style={{ color: "#475569", marginBottom: 16 }}>Este plan ya está activo. Entra con tu cuenta.</Typography>
                <Button component={RouterLink} to="/login" fullWidth variant="contained" color="primary" size="large">Iniciar sesión</Button>
              </>
            )}
            {info.status === "pending" && (
              <Typography variant="body2" style={{ color: "#475569" }}>Aún estamos confirmando el pago de este pedido. Vuelve a abrir el enlace en unos minutos.</Typography>
            )}

            {info.status === "used" && publicidad && (
              <Button href={info.siteUrl} target="_blank" rel="noopener noreferrer" fullWidth variant="outlined" color="primary" style={{ marginTop: 8 }}>Ir a Publicidad IA</Button>
            )}

            {info.status === "ready" && publicidad && (
              <form onSubmit={activatePublicidad}>
                <Typography variant="body2" style={{ color: "#475569", marginBottom: 8, lineHeight: 1.6 }}>
                  Tu pago está confirmado. Escribe tu correo: si es nuevo, creamos tu cuenta con el plan activo; si ya tienes cuenta, usa el mismo correo con el que entras y el plan se suma.
                </Typography>
                <TextField fullWidth margin="normal" variant="outlined" label="Correo" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }} />
                <TextField fullWidth margin="normal" variant="outlined" label="WhatsApp (opcional)" inputMode="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value.replace(/[^\d]/g, ""))}
                  helperText="Con indicativo, solo números. Ej: 573001234567"
                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }} />
                <Button type="submit" fullWidth variant="contained" color="primary" size="large" disabled={busy || !email} style={{ marginTop: 8 }}>
                  {busy ? "Activando…" : "Activar mi plan"}
                </Button>
              </form>
            )}

            {info.status === "ready" && !publicidad && mode === "choose" && (
              <>
                <Typography variant="body2" style={{ color: "#475569", marginBottom: 16, lineHeight: 1.6 }}>
                  Tu pago está confirmado. Elige cómo activarlo: en menos de un minuto queda listo.
                </Typography>
                <Button component={RouterLink} to={`/signup?activar=${encodeURIComponent(token)}`} fullWidth variant="contained" color="primary" size="large" startIcon={<PersonAdd />} style={{ marginBottom: 12 }}>
                  Crear mi cuenta nueva
                </Button>
                <Button fullWidth variant="outlined" color="primary" size="large" startIcon={<VpnKey />} onClick={() => { setMode("existing"); setError(""); }}>
                  Ya tengo cuenta WaToolX
                </Button>
              </>
            )}

            {info.status === "ready" && !publicidad && mode === "existing" && (
              <form onSubmit={activateExisting}>
                <Typography variant="body2" style={{ color: "#475569", marginBottom: 8 }}>
                  Entra con el usuario <b>administrador</b> de tu empresa. El plan se suma a tu vencimiento actual (aunque tu cuenta esté vencida).
                </Typography>
                <TextField fullWidth margin="normal" variant="outlined" label="Correo" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }} />
                <TextField fullWidth margin="normal" variant="outlined" label="Contraseña" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }} />
                <Button type="submit" fullWidth variant="contained" color="primary" size="large" disabled={busy || !email || !password} style={{ marginTop: 8 }}>
                  {busy ? "Activando…" : "Activar plan en mi cuenta"}
                </Button>
                <Button fullWidth onClick={() => setMode("choose")} style={{ marginTop: 8 }}>Volver</Button>
              </form>
            )}
          </>
        )}
        <Box mt={3}><AuthCredit /></Box>
      </Paper>
    </Box>
  );
}
